// import React, { useEffect, useRef, useState } from "react";
// import Button from "../Buttons/Button";

// const AudioPlayer = ({ file }) => {
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [currentTime, setCurrentTime] = useState(0);
//   const [duration, setDuration] = useState(0);
//   const audioRef = useRef(null);

//   useEffect(() => {
//     if (audioRef.current) {
//       audioRef.current.addEventListener("timeupdate", handleTimeUpdate);
//       audioRef.current.addEventListener("loadedmetadata", handleLoadedMetadata);
//     }
//     return () => {
//       if (audioRef.current) {
//         audioRef.current.removeEventListener("timeupdate", handleTimeUpdate);
//         audioRef.current.removeEventListener(
//           "loadedmetadata",
//           handleLoadedMetadata,
//         );
//       }
//     };
//   }, []);

//   const handleTimeUpdate = () => {
//     setCurrentTime(audioRef.current.currentTime);
//   };

//   const handleLoadedMetadata = () => {
//     setDuration(audioRef.current.duration);
//   };

//   const formatTime = (time) => {
//     const minutes = Math.floor(time / 60);
//     const seconds = Math.floor(time % 60);
//     return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
//   };

//   return (
//     <div className="p-1 flex gap-1">
//       <Button
//         type="icon"
//         icon={isPlaying ? "pause" : "play_arrow"}
//         color="primary"
//         variant="outlined"
//         size="sm"
//         onClick={() => {
//           if (isPlaying) {
//             audioRef.current.pause();
//           } else {
//             audioRef.current.play();
//           }
//           setIsPlaying(!isPlaying);
//         }}
//       />
//       <div className="flex gap-2 items-center border-[1px] rounded-full border-gray-400 text-xs pl-3 pr-1.5">
//         <div>{formatTime(currentTime)}</div>
//         <div className="flex-1 w-full min-w-16">
//           <div className="w-full h-1.5 bg-gray-300 rounded-full"></div>
//         </div>
//         <div>{formatTime(duration)}</div>
//         <div>
//           <Button
//             type="icon"
//             icon={"download"}
//             color="dark"
//             variant="text"
//             size="sm"
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AudioPlayer;

import React, { useEffect, useRef, useState } from "react";
import Button from "../Buttons/Button";
import { useAudioPlayerGroup } from "./AudioPlayerGroup";

const AudioPlayer = ({
  file,
  download: { isHideDownload = false, isDownloadable = true } = {},
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const playerIdRef = useRef(`ap_${Math.random().toString(36).slice(2)}`);
  const audioPlayerGroup = useAudioPlayerGroup();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handlePlay = () => {
      setIsPlaying(true);
      if (audioPlayerGroup) {
        audioPlayerGroup.requestPlay(playerIdRef.current);
      }
    };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioPlayerGroup]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audioPlayerGroup || !audio) return;

    const playerId = playerIdRef.current;
    const pause = () => audio.pause();

    audioPlayerGroup.register(playerId, pause);
    return () => audioPlayerGroup.unregister(playerId);
  }, [audioPlayerGroup]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const handleSeek = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = pct * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!audio.paused) {
      audio.pause();
      return;
    }

    const playerId = playerIdRef.current;
    if (audioPlayerGroup) {
      audioPlayerGroup.requestPlay(playerId);
    }
    void audio.play().catch(() => {});
  };

  const progressPct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={`p-1 flex gap-1 items-center ${isPlaying ? "opacity-100" : " opacity-90"}`}
    >
      <audio ref={audioRef} src={file} preload="metadata" />

      <Button
        type="icon"
        icon={isPlaying ? "pause" : "play_arrow"}
        color={isPlaying ? "primary" : "primary"}
        variant={isPlaying ? "contained" : "outlined"}
        size="sm"
        onClick={togglePlay}
      />

      <div
        className={`flex gap-2 items-center border border-gray-400 rounded-full text-xs pl-3 pr-1.5 h-9 ${isPlaying ? "bg-gray-100" : ""}`}
      >
        <div className="tabular-nums text-center text-xs min-w-10 w-fit">
          {formatTime(currentTime)}
        </div>

        <div
          className="flex-1 w-full min-w-20 relative h-1.5 cursor-pointer group"
          onClick={handleSeek}
        >
          <div className="absolute inset-0 bg-gray-300 rounded-full" />
          <div
            className="absolute top-0 left-0 h-full bg-primary rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `${progressPct}%` }}
          />
        </div>

        <div className="tabular-nums text-center text-xs min-w-10 w-fit">
          {formatTime(duration)}
        </div>
        {!isHideDownload && ( // ← no need to check `download` anymore
          <Button
            type="icon"
            icon="download"
            color="dark"
            variant="text"
            size="sm"
            disabled={!isDownloadable}
            onClick={() => {
              const a = document.createElement("a");
              a.href = file;
              a.download = "";
              a.click();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;
